import { useEffect, useRef, useState, useContext } from "react";
import { getCustomisationValue, getGroupData } from "../../../EditablePreview";
import InlineProgressBar, { convertCustomisationDataToBarSettings } from "../../progress-bar/inline-progress-bar";
import VisualisedNode, { IMAGE_MODES } from "../../VisualisedNode";
import { filesRoute } from "../../../../../../../../api/routes";
import config from "../../../../../../../../config";
import { CampaignContext } from "../../../../../../../../contexts/CampaignContext";
import { CUSTOMISED_URL_MAP } from "../../../../../../../../config/FeatureDashboardVarMap";
import './WaveHands.scss';

const MAX_HANDS = 70;
const HANDS_APPEAR_DELAY = 300; // milliseconds
const HANDS_RESET_DELAY = 3000; // milliseconds

// Helper function to construct image URL from filename object (matches ImageField logic)
const getImageUrl = (filenameObj, campaignId) => {
    if (!filenameObj) {
        console.log('WaveHands: getImageUrl - no filenameObj');
        return '';
    }
    
    console.log('WaveHands: getImageUrl - filenameObj:', filenameObj, 'campaignId:', campaignId, 'filesRoute:', filesRoute);
    
    // If it's already a string URL, return it
    if (typeof filenameObj === 'string' && (filenameObj.startsWith('http') || filenameObj.startsWith('/'))) {
        console.log('WaveHands: getImageUrl - returning string URL:', filenameObj);
        return filenameObj;
    }
    
    // Extract filename and metadata
    const filename = typeof filenameObj === 'object' ? filenameObj.filename : filenameObj;
    const isDefault = filenameObj?.isDefault || false;
    const type = filenameObj?.type || null;
    
    console.log('WaveHands: getImageUrl - filename:', filename, 'isDefault:', isDefault, 'type:', type);
    
    // Default images come from CloudFront CDN
    if (isDefault && type) {
        const url = `${config.cloudfrontBaseUrl}/feature-assets/wave/${type}/${filename}`;
        console.log('WaveHands: getImageUrl - returning CloudFront URL:', url);
        return url;
    }
    
    // Custom uploaded images use filesRoute
    // Need to add extension since filename is stored without extension
    // Try common image extensions - this could be improved by storing extension info
    const extensions = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
    const baseUrl = campaignId 
        ? `${filesRoute}/feature-assets/campaigns/${campaignId}/${CUSTOMISED_URL_MAP.image.dir}/${filename}`
        : `${filesRoute}/feature-assets/${CUSTOMISED_URL_MAP.image.dir}/${filename}`;
    
    // If filename already has extension, use it as is
    if (filename.includes('.')) {
        console.log('WaveHands: getImageUrl - returning filesRoute URL with existing extension:', baseUrl);
        return baseUrl;
    }
    
    // Otherwise, try with .png extension (most common case)
    const url = `${baseUrl}.png`;
    console.log('WaveHands: getImageUrl - returning filesRoute URL with png extension:', url);
    return url;
};

const WaveHands = (props) => {
    // console.log("WaveHands props:", props);
    const node = props.node;
    
    // Get campaignId from context
    const [campaignContext] = useContext(CampaignContext);
    const campaignId = campaignContext?.campaignData?._id;

    const handsGroupData = getGroupData(props.node.customisationVars, 'hands.objects.handObject', -1);
    // console.log("Hands group data:", handsGroupData);

    const [randomHands, setRandomHands] = useState([]);
    const randomHands_ref = useRef(randomHands);
    const [handsInterval, setHandsInterval] = useState(null);
    const handsInterval_ref = useRef(handsInterval);
    const [handsResetTimeout, setHandsResetTimeout] = useState(null);
    const handsResetTimeout_ref = useRef(handsResetTimeout);
    useEffect(() => {
        randomHands_ref.current = randomHands;
        handsInterval_ref.current = handsInterval;
        handsResetTimeout_ref.current = handsResetTimeout;
    }, [randomHands, handsInterval, handsResetTimeout]);
    const clearAllHands = () => {
        //if (props.timelinePlayheadStep === 0) {
            setRandomHands([]);
            setHandsInterval(setInterval(addRandomHand, HANDS_APPEAR_DELAY));
        /*}  else {
            setHandsResetTimeout(setTimeout(() => {
                clearAllHands();
            }, HANDS_RESET_DELAY));
        } */
    }
    const addRandomHand = () => {
        /* if (props.timelinePlayheadStep > 0 && randomHands_ref.current.length < MAX_HANDS) {
            while (randomHands_ref.current.length < MAX_HANDS) {
                setRandomHands(prev => [...prev, {
                    index: Math.floor(Math.random() * handsGroupData.length),
                    x: (Math.random() * 100) + '%',
                    y: (Math.random() * 40) + 60 + '%',
                }]);
            }
        } else */
        if (randomHands_ref.current.length < MAX_HANDS) {

            const handsFrequency = [];
            for (let i = 0; i < handsGroupData.length; i++) {
                for (let j = 0; j < (handsGroupData[i].appearFrequency || 1); j++) {
                    handsFrequency.push(i);
                }
            }

            setRandomHands(prev => [...prev, {
                index: handsFrequency[Math.floor(Math.random() * handsFrequency.length)],
                x: (Math.random() * 100) + '%',
                y: (Math.random() * 40) + 60 + '%',
            }]);
        } else {
            clearInterval(handsInterval_ref.current);
            setHandsInterval(null);
            if (handsResetTimeout_ref.current) {
                clearTimeout(handsResetTimeout_ref.current);
            }
            setHandsResetTimeout(setTimeout(() => {
                clearAllHands();
            }, HANDS_RESET_DELAY));
        }
    }
    useEffect(() => {
        console.log("WaveHands useEffect - timelinePlayheadStep:", props.timelinePlayheadStep, props);
        if (props.timelinePlayheadStep === 0) {
            clearInterval(handsInterval_ref.current);
            if (handsResetTimeout_ref.current) {
                clearTimeout(handsResetTimeout_ref.current);
            }
            clearAllHands();
        } else {
            clearInterval(handsInterval_ref.current);
            setHandsInterval(null);
            if (handsResetTimeout_ref.current) {
                clearTimeout(handsResetTimeout_ref.current);
            }
            const handsArray = randomHands;
            while (handsArray.length < MAX_HANDS) {
                handsArray.push({
                    index: Math.floor(Math.random() * handsGroupData.length),
                    x: (Math.random() * 100) + '%',
                    y: (Math.random() * 40) + 60 + '%',
                });
            }
        }
        return () => {
            clearInterval(handsInterval);
            clearTimeout(handsResetTimeout);
        };
    }, [props.timelinePlayheadStep, handsGroupData.length]);

    return (
        <>
            {
                randomHands.map((hand, index) => {
                    const handData = handsGroupData?.[hand.index];
                    const imageUrl = getImageUrl(handData?.filename, campaignId);
                    console.log('WaveHands: rendering hand', index, 'handData:', handData, 'imageUrl:', imageUrl, 'campaignId:', campaignId);
                    
                    return (
                        <img 
                            key={index}
                            src={imageUrl}
                            alt={`Hand ${index}`}
                            className="wave-hand"
                            style={{
                                position: 'absolute',
                                left: hand.x,
                                top: hand.y,
                                transform: `translate(-50%, -50%) scale(${props.previewScale}) scale(${handData?.imageScale ?? 100}%)`,
                                opacity: (props.node === props.visualisedNode && props.activeNode !== props.node && props.activeNode.dataIndex !== hand.index) ? 0.25 : 1,
                            }}
                            onClick={
                                (e) => {
                                    props.activateNodeFunction(node.subNodes[hand.index]);
                                    e.stopPropagation();
                                }
                            }
                        />
                    );
                })
            }
            {/*node === props.visualisedNode &&
                <div className="wave-hands-list-holder">
                    {handsGroupData && handsGroupData.map((hand, index) => {
                        return <VisualisedNode key={index} {...props} node={node} imageMode={IMAGE_MODES.naturalSize} index={index} contentNode={true} />
                    })}
                </div>
            */}
        </>
    )
}

export default WaveHands;
